// MONA-maintained work streams: parts of the platform that run as backend services rather than
// organigram nodes. They never write executorAgentTokenUsage, so neither the pricing dashboard nor
// the organigram usage panel can see them from agent telemetry alone. They are surfaced read-only:
// the customer can see the work and its volume but cannot configure it.
//
// Pure and Angular-free so it is unit-testable without the CustomerdatabaseService import cycle.

export type ManagedWorkflowHealth = 'live' | 'idle' | 'stalled' | 'unknown';

export interface CollectionRef {
  collection: string;
  label: string;
  timestampField?: string;
}

export interface ManagedWorkflowDescriptor {
  id: string;
  name: string;
  description: string;
  icon?: string;
  // What proves the service is running. Chosen as the most frequently written signal.
  heartbeat: CollectionRef;
  // Additional freshness signals, shown alongside the heartbeat.
  activity: CollectionRef[];
  // Counted with server-side aggregation; these collections are far too large to read.
  volume: CollectionRef[];
  statusBreakdown?: {
    collection: string;
    field: string;
    values: string[];
    // Statuses that represent work landing successfully, used for the yield figure.
    successValues: string[];
  };
  metering?: {
    collection: string;
    timestampField: string;
    unit: string;
    unitPlural: string;
    senderPrefix?: string;
  };
}

export interface ManagedWorkflowReading {
  descriptor: ManagedWorkflowDescriptor;
  heartbeatISO: string | null;
  activityISO: Record<string, string | null>;
  counts: Record<string, number | null>;
  statusCounts: Record<string, number | null>;
  errors: string[];
}

export interface ManagedWorkflowView {
  id: string;
  name: string;
  description: string;
  health: ManagedWorkflowHealth;
  healthLabel: string;
  lastSignalISO: string | null;
  minutesSinceSignal: number | null;
  volume: { label: string; count: number | null }[];
  statuses: { label: string; count: number; share: number }[];
  totalStatusCount: number;
  successCount: number;
  successShare: number;
  errors: string[];
}

// A service writing within two hours is running. Beyond a day it has stopped, not paused.
export const LIVE_WITHIN_MINUTES = 120;
export const IDLE_WITHIN_MINUTES = 1440;

export const SHIFT_FILL_WORKFLOW: ManagedWorkflowDescriptor = {
  id: 'shift-fill',
  name: 'Shift agent',
  description: 'Pulls recurring shift patterns, broadcasts open shifts to matched candidates and '
    + 'tracks acceptance. Runs as a MONA-managed backend service, not as an organigram node, so its '
    + 'volume does not appear in agent execution counts.',
  heartbeat: { collection: 'bluearrow_sync_checkpoints', label: 'Sync checkpoint', timestampField: 'last_synced_at' },
  activity: [
    { collection: 'shift_invitations', label: 'Invitation activity', timestampField: 'updated' },
    { collection: 'regular_shifts', label: 'Pattern refresh', timestampField: 'updated' }
  ],
  volume: [
    { collection: 'shift_invitations', label: 'Invitations broadcast' },
    { collection: 'shifts', label: 'Shifts tracked' },
    { collection: 'regular_shifts', label: 'Recurring patterns' },
    { collection: 'job_embeddings', label: 'Match vectors' }
  ],
  statusBreakdown: {
    collection: 'shift_invitations',
    field: 'status',
    values: ['Broadcasting', 'Accepted', 'Complete', 'Declined', 'Stale', 'Failed'],
    successValues: ['Accepted', 'Complete']
  },
  metering: {
    collection: 'shift_invitations',
    timestampField: 'created_at',
    unit: 'invitation sent',
    unitPlural: 'invitations sent',
    senderPrefix: 'workflow_shift'
  }
};

export const MATCHING_PLUS_WORKFLOW: ManagedWorkflowDescriptor = {
  id: 'matching-plus',
  name: 'Matching Plus',
  description: 'Matches each interviewed candidate against the open internal jobs, adds public transport '
    + 'travel times across the day and a score per criterion, and attaches the result to the application '
    + 'in the ATS. Runs as a MONA-managed backend service on top of the standard profile and matching.',
  icon: 'git-compare-outline',
  heartbeat: { collection: 'matching_plus_events', label: 'Last enrichment', timestampField: 'created' },
  activity: [],
  volume: [{ collection: 'matching_plus_events', label: 'Candidates enriched' }],
  metering: {
    collection: 'matching_plus_events',
    timestampField: 'created',
    unit: 'candidate enriched',
    unitPlural: 'candidates enriched'
  }
};

export const MANAGED_WORKFLOWS: ManagedWorkflowDescriptor[] = [SHIFT_FILL_WORKFLOW, MATCHING_PLUS_WORKFLOW];

export function managedIcon(id: string): string {
  return MANAGED_WORKFLOWS.find(w => w.id === id)?.icon || 'calendar-outline';
}

export function isDeployed(view: ManagedWorkflowView): boolean {
  return !!view.lastSignalISO || view.volume.some(v => (v.count || 0) > 0) || view.errors.length > 0;
}

export function meteringWhere(field: string, from: Date | null, to: Date | null): any {
  const bound = (op: string, d: Date) => ({
    fieldFilter: { field: { fieldPath: field }, op, value: { timestampValue: d.toISOString() } }
  });
  const filters = [bound('GREATER_THAN_OR_EQUAL', from || new Date(0)), ...(to ? [bound('LESS_THAN', to)] : [])];
  return filters.length > 1 ? { compositeFilter: { op: 'AND', filters } } : filters[0];
}

export function scalarFields(fields: Record<string, any> | null | undefined): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  Object.entries(fields || {}).forEach(([k, v]) => {
    if (v && 'booleanValue' in v) { out[k] = v.booleanValue; }
    else if (v && 'stringValue' in v) { out[k] = v.stringValue; }
    else if (v && 'integerValue' in v) { out[k] = Number(v.integerValue); }
    else if (v && 'doubleValue' in v) { out[k] = v.doubleValue; }
    else if (v && 'timestampValue' in v) { out[k] = v.timestampValue; }
  });
  return out;
}

export function managedWorkflowOfSender(userId: unknown): string | null {
  if (typeof userId !== 'string' || !userId) { return null; }
  const w = MANAGED_WORKFLOWS.find(d => d.metering?.senderPrefix && userId.startsWith(d.metering.senderPrefix));
  return w ? w.id : null;
}

export function parseBackendTimestamp(value: unknown): Date | null {
  if (!value) { return null; }
  if (typeof value === 'object' && typeof (value as any).toDate === 'function') {
    const d = (value as any).toDate();
    return d instanceof Date && !isNaN(d.getTime()) ? d : null;
  }
  // The backend writes some timestamps as "...+00:00Z" - both an offset and a Z, which Date rejects.
  const raw = typeof value === 'string' ? value.replace(/([+-]\d{2}:\d{2})Z$/, '$1') : value;
  const d = new Date(raw as any);
  return isNaN(d.getTime()) ? null : d;
}

export function minutesSince(iso: string | null, now: number = Date.now()): number | null {
  const d = parseBackendTimestamp(iso);
  if (!d) { return null; }
  return (now - d.getTime()) / 60000;
}

export function workflowHealth(iso: string | null, now: number = Date.now()): ManagedWorkflowHealth {
  const mins = minutesSince(iso, now);
  if (mins === null) { return 'unknown'; }
  if (mins < 0) { return 'live'; }
  if (mins <= LIVE_WITHIN_MINUTES) { return 'live'; }
  if (mins <= IDLE_WITHIN_MINUTES) { return 'idle'; }
  return 'stalled';
}

export function healthLabel(health: ManagedWorkflowHealth, minutes: number | null): string {
  if (health === 'unknown') { return 'No signal'; }
  const ago = minutes === null ? ''
    : minutes < 60 ? `${Math.max(0, Math.round(minutes))} min ago`
      : minutes < 2880 ? `${(minutes / 60).toFixed(1)} h ago`
        : `${(minutes / 1440).toFixed(1)} d ago`;
  if (health === 'live') { return `Running · last write ${ago}`; }
  if (health === 'idle') { return `Quiet · last write ${ago}`; }
  return `Stalled · last write ${ago}`;
}

// The freshest of the heartbeat and any activity signal. A service can be mid-cycle on one
// collection while another has not been touched for hours, so the newest wins.
export function freshestSignal(reading: ManagedWorkflowReading): string | null {
  const all = [reading.heartbeatISO, ...Object.values(reading.activityISO || {})];
  let best: string | null = null;
  let bestT = -Infinity;
  all.forEach(iso => {
    const d = parseBackendTimestamp(iso);
    if (d && d.getTime() > bestT) { bestT = d.getTime(); best = iso; }
  });
  return best;
}

export function toView(reading: ManagedWorkflowReading, now: number = Date.now()): ManagedWorkflowView {
  const lastSignalISO = freshestSignal(reading);
  const minutes = minutesSince(lastSignalISO, now);
  const health = workflowHealth(lastSignalISO, now);

  const statusEntries = Object.entries(reading.statusCounts || {})
    .filter(([, c]) => typeof c === 'number') as [string, number][];
  const totalStatusCount = statusEntries.reduce((s, [, c]) => s + c, 0);
  const success = reading.descriptor.statusBreakdown?.successValues || [];
  const successCount = statusEntries
    .filter(([label]) => success.indexOf(label) !== -1)
    .reduce((s, [, c]) => s + c, 0);

  return {
    id: reading.descriptor.id,
    name: reading.descriptor.name,
    description: reading.descriptor.description,
    health,
    healthLabel: healthLabel(health, minutes),
    lastSignalISO,
    minutesSinceSignal: minutes,
    volume: reading.descriptor.volume.map(v => ({
      label: v.label,
      count: reading.counts ? (reading.counts[v.collection] ?? null) : null
    })),
    statuses: statusEntries
      .map(([label, count]) => ({
        label,
        count,
        share: totalStatusCount > 0 ? count / totalStatusCount : 0
      }))
      .sort((a, b) => b.count - a.count),
    totalStatusCount,
    successCount,
    successShare: totalStatusCount > 0 ? successCount / totalStatusCount : 0,
    errors: reading.errors || []
  };
}
